import type { GlobalTransaction } from '@bloque/sdk';
import type {
  Asset,
  Movement,
  MovementStatus,
  MovementType,
} from './mock-data';

const ASSET_KEY_MAP: Record<string, Asset> = {
  COP: 'COP',
  COPM: 'COP',
  DUSD: 'USD',
  USD: 'USD',
  KSM: 'KSM',
};

function toAsset(rawAsset: string): Asset | null {
  const [assetKey] = rawAsset.split('/');
  return ASSET_KEY_MAP[assetKey] ?? null;
}

function toAmount(rawAmount: string, rawAsset: string): number {
  const [, precisionStr] = rawAsset.split('/');
  const precision = Number.parseInt(precisionStr, 10);
  const parsed = Number.parseInt(rawAmount, 10);

  if (Number.isNaN(parsed)) return 0;
  if (Number.isNaN(precision)) return parsed;

  return parsed / 10 ** precision;
}

function toStatus(status: string): MovementStatus {
  const normalized = status.toLowerCase();

  if (
    normalized.includes('pending') ||
    normalized.includes('queued') ||
    normalized.includes('process')
  ) {
    return 'pending';
  }

  if (
    normalized.includes('confirm') ||
    normalized.includes('settled') ||
    normalized.includes('success') ||
    normalized.includes('complete')
  ) {
    return 'completed';
  }

  return 'failed';
}

function toType(transaction: GlobalTransaction): MovementType {
  if (transaction.type === 'deposit') return 'topup';
  if (transaction.type === 'withdraw') return 'withdraw';
  if (transaction.type === 'transfer') return 'send';

  if (transaction.railName.toLowerCase().includes('card')) return 'card';

  return 'convert';
}

export function mapGlobalTransactionToMovement(
  transaction: GlobalTransaction,
): Movement | null {
  const asset = toAsset(transaction.asset);
  if (!asset) return null;

  const uniqueId = [
    transaction.reference,
    transaction.createdAt,
    transaction.amount,
  ].join('-');

  return {
    id: uniqueId,
    type: toType(transaction),
    asset,
    amount: toAmount(transaction.amount, transaction.asset),
    fee: 0,
    status: toStatus(transaction.status),
    createdAt: transaction.createdAt,
    reference: transaction.reference,
    counterparty:
      transaction.direction === 'in'
        ? transaction.fromAccountId
        : transaction.toAccountId,
    direction: transaction.direction === 'in' ? 'incoming' : 'outgoing',
  };
}
